import { put, select, call } from "redux-saga/effects";
import _ from "lodash";
import { editActions } from "../actions/editActions";
import { mainActions } from "../actions/mainActions";
import { pdApiActions } from "../actions/pdApiActions";
import { editViewSelectors, pdApiSelectors, ioConfigSelectors } from "../utils/selectors";
import { hideNotification, showError, showInfo, showSuccess, showWarning } from "../utils/utils";
import { pdApiPut, pdApiPost, pdApiDelete, pdApiGet, pdApiPostBindings } from "../utils/pdApi";
import {
  noticationMessages,
  getMediaType,
  createEmptyStandardAction,
  createEmptyStandardNode
} from "../utils/pdApiUtils";
import { ioConfigActions } from "../actions/ioConfigActions";
import { fetchParameter, fetchEscalatorParameters, fetchEscalatorFaults } from "./pdApiSagas";

const updateArray = (items: Api.AnyItem[], updatedItem: Api.AnyItem): Api.AnyItem[] => {
  if (updatedItem.id !== undefined) {
    return items.map(item => {
      if (item.id === updatedItem.id) {
        return { ...updatedItem };
      } else {
        return item;
      }
    });
  } else if (updatedItem.boardTypeId !== undefined) {
    return items.map(item => {
      if (item.boardTypeId === updatedItem.boardTypeId) {
        return { ...updatedItem };
      } else {
        return item;
      }
    });
  } else if (updatedItem.enumValue !== undefined) {
    return items.map(item => {
      if (item.enumValue === updatedItem.enumValue) {
        return { ...updatedItem };
      } else {
        return item;
      }
    });
  } else if (updatedItem.value !== undefined) {
    return items.map(item => {
      if (item.value === updatedItem.value) {
        return { ...updatedItem };
      } else {
        return item;
      }
    });
  } else if (updatedItem.locale !== undefined) {
    return items.map(item => {
      if (item.locale === updatedItem.locale) {
        return { ...updatedItem };
      } else {
        return item;
      }
    });
  } else if (updatedItem.enumID !== undefined) {
    return items.map(item => {
      if (item.enumID === updatedItem.enumID) {
        return { ...updatedItem };
      } else {
        return item;
      }
    });
  } else {
    return items;
  }
};
const updateStandardBoardArrayWithNode = (
  boards: Api.StandardBoard[],
  updatedNode: Api.StandardNode
): Api.StandardBoard[] =>
  boards.map((board: Api.StandardBoard) => {
    if (board.id !== updatedNode.parentBoardId) {
      return board;
    }
    const nodes = board.standardNodes?.map(node =>
      node.id === updatedNode.id ? updatedNode : node
    );
    return { ...board, standardNodes: nodes ?? [] };
  });

const updateStandardPointWithAction = (
  standardPoints: Api.StandardPoint[],
  updatedAction: Api.StandardAction
): Api.StandardPoint[] => {
  const updatedPoints = standardPoints.map(point => {
    let updatedActions;
    if (point.standardActions?.length === 0) {
      updatedActions = [updatedAction];
    } else {
      updatedActions = point.standardActions?.map(action =>
        action.id === updatedAction.id ? updatedAction : action
      );
    }
    return point.id === updatedAction.parentPointId
      ? { ...point, standardActions: updatedActions ?? [] }
      : point;
  });

  return updatedPoints;
};
const updateIoPointWithAction = (
  ionodePoints: Api.IoNodePoint[],
  updatedAction: Api.StandardAction
): Api.IoNodePoint[] => {
  const updatedPoints = ionodePoints.map(point => {
    let updatedActions;
    if (point.ionodePointActions?.length === 0) {
      updatedActions = [updatedAction];
    } else {
      updatedActions = point.ionodePointActions?.map(action =>
        action.id === updatedAction.id ? updatedAction : action
      );
    }
    return point.id === updatedAction.parentPointId
      ? { ...point, ionodePointActions: updatedActions ?? [] }
      : point;
  });

  return updatedPoints;
};
const addActionToStandardPoints = (
  standardPoints: Api.StandardPoint[],
  updatedAction: Api.StandardAction
): Api.StandardPoint[] => {
  const updatedPoints = standardPoints.map(point =>
    point.id === updatedAction.parentPointId
      ? {
        ...point,
        standardActions: point.standardActions ? [...point.standardActions, updatedAction] : null
      }
      : point
  );
  return updatedPoints;
};

const addNodeToStandardBoards = (
  standardBoards: Api.StandardBoard[],
  newNode: Api.StandardNode
): Api.StandardBoard[] => {
  const updatedStandardBoards = standardBoards.map(board =>
    board.id === newNode.parentBoardId
      ? {
        ...board,
        standardNodes: board.standardNodes ? [...board.standardNodes, newNode] : null
      }
      : board
  );

  return updatedStandardBoards;
};
const addActionToIonodePoints = (
  ionodePoints: Api.IoNodePoint[],
  updatedAction: Api.IoNodePointAction
): Api.IoNodePoint[] => {
  const updatedPoints = ionodePoints.map(point =>
    point.id === updatedAction.parentPointId
      ? {
        ...point,
        ionodePointActions: point.ionodePointActions
          ? [...point.ionodePointActions, updatedAction]
          : null
      }
      : point
  );
  return updatedPoints;
};

/** Update item */
export function* updateSaga(action: ReturnType<typeof editActions.update>): Api.AnyItem {
  const option = action.payload;
  const queryOption = action.payload === "escalatorParameters"
    ? "escalatorParameters"
    : action.payload === "doorParameters"
      ? "doorParameters"
      : action.payload.includes("Parameter")
        ? "parameters" : action.payload === "apmFaults"
          ? "faults" : (action.payload === "driveFaults")
            ? "faults" : (action.payload === "liftFaults")
              ? "faults" : (action.payload === "groupFaults")
                ? "faults" : (action.payload === "signalizationFaults")//check this is needed
                  ? "faults" : action.payload;
  const pdApi: State.PdApi = yield select(pdApiSelectors.pdApi);
  const ioConfig: State.IoConfig = yield select(ioConfigSelectors.ioConfig);
  const editedItem = yield select(editViewSelectors.editedObject);
  const parentItem = yield select(editViewSelectors.parentObject);
  if (option === "ioBasicDataTypes") {
    editedItem.iodataSections = _.map(editedItem.iodataSections, item => {
      if (item.id) {
        return { id: item.id };
      } else {
        return { id: item };
      }
    });
  }
  if (option === "ioConfigurations") {
    // Empty the ioboards properties, otherwise the PUT request can be too large (at least after cloning)
    editedItem.ioboards = [];
    editedItem.ioboardsGrouped = {};
  }

  if (option === "standardActions" || option === "ionodePointActions") {
    yield put(ioConfigActions.setLocalFetchingStatus("Updating"));
  } else {
    yield put(mainActions.setFetchingStatus("Updating"));
  }

  if (option === "ionodePoints") {
    const index = ioConfig.ionodePoints.findIndex(item => item.id === editedItem.id);

    ioConfig.ionodePoints[index].ionodePointActions?.forEach(item => {
      if (!_.find(editedItem.ionodePointActions, item)) {
        editedItem.ionodePointActions.push(item);
      }
    });
  }
  if (option === "standardPoints") {
    const index = ioConfig.standardPoints.findIndex(item => item.id === editedItem.id);

    ioConfig.standardPoints[index].standardActions?.forEach(item => {
      if (!_.find(editedItem.standardActions, item)) {
        editedItem.standardActions.push(item);
      }
    });
  }

  try {
    const mediaType = getMediaType(queryOption, editedItem, pdApi);
    // Todo : This function return Type need to be fixed
    const updatedItem = yield call(pdApiPut, queryOption, editedItem, mediaType);
    switch (option) {
      case "faults": {
        const faults = updateArray(pdApi.faults, updatedItem);
        yield put(pdApiActions.setFaults(faults));
        const driveFaults = updateArray(pdApi.driveFaults ?? [], updatedItem);
        yield put(pdApiActions.setDriveFaults(driveFaults));
        const liftFaults = updateArray(pdApi.liftFaults ?? [], updatedItem);
        yield put(pdApiActions.setLiftFaults(liftFaults));
        const groupFaults = updateArray(pdApi.groupFaults ?? [], updatedItem);
        yield put(pdApiActions.setGroupFaults(groupFaults));
        break;
      }
      case "driveFaults": {
        const driveFaults = updateArray(pdApi.driveFaults ?? [], updatedItem);
        yield put(pdApiActions.setDriveFaults(driveFaults));
        const faults = updateArray(pdApi.faults, updatedItem);
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "liftFaults": {
        const liftFaults = updateArray(pdApi.liftFaults ?? [], updatedItem);
        yield put(pdApiActions.setLiftFaults(liftFaults));
        const faults = updateArray(pdApi.faults, updatedItem);
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "groupFaults": {
        const groupFaults = updateArray(pdApi.groupFaults ?? [], updatedItem);
        yield put(pdApiActions.setGroupFaults(groupFaults));
        const faults = updateArray(pdApi.faults, updatedItem);
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "networkFaults": {
        const networkFaults = updateArray(pdApi.networkFaults ?? [], updatedItem);
        yield put(pdApiActions.setNetworkFaults(networkFaults));
        const faults = updateArray(pdApi.faults, updatedItem);
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "monitoringFaults": {
        const monitoringFaults = updateArray(pdApi.monitoringFaults ?? [], updatedItem);
        yield put(pdApiActions.setMonitoringFaults(monitoringFaults));
        const faults2 = updateArray(pdApi.faults, updatedItem);
        yield put(pdApiActions.setFaults(faults2));
        break;
      }
      case "escalatorFaults": {
        const escalatorFaults = updateArray(pdApi.escalatorFaults, updatedItem);
        yield put(pdApiActions.setEscalatorFaults(escalatorFaults));
        break;
      }
      case "doorFaults": {
        const doorFaults = updateArray(pdApi.doorFaults, updatedItem);
        yield put(pdApiActions.setDoorFaults(doorFaults));
        break;
      }
      case "nonKoneFaults": {
        const nonKoneFaults = updateArray(pdApi.nonKoneFaults, updatedItem);
        yield put(pdApiActions.setNonKoneFaults(nonKoneFaults));
        break;
      }
      case "liftParameters": {
        const liftParameters = updateArray(pdApi.liftParameters, updatedItem);
        yield put(pdApiActions.setLiftParameters(liftParameters));
        break;
      }
      case "driveParameters": {
        const driveParameters = updateArray(pdApi.driveParameters, updatedItem);
        yield put(pdApiActions.setDriveParameters(driveParameters));
        break;
      }
      case "groupParameters": {
        const groupParameters = updateArray(pdApi.groupParameters, updatedItem);
        yield put(pdApiActions.setGroupParameters(groupParameters));
        break;
      }
      case "safetyParameters": {
        const safetyParameters = updateArray(pdApi.safetyParameters, updatedItem);
        yield put(pdApiActions.setSafetyParameters(safetyParameters));
        break;
      }
      case "escalatorParameters": {
        const escalatorParameters = updateArray(pdApi.escalatorParameters, updatedItem);
        yield put(pdApiActions.setEscalatorParameters(escalatorParameters));
        break;
      }
      case "apmParameters": {
        const apmParameters = updateArray(pdApi.apmParameters ?? [], updatedItem);
        yield put(pdApiActions.setAPMParameters(apmParameters));
        break;
      }
      case "apmFaults": {
        const apmFaults = updateArray(pdApi.apmFaults ?? [], updatedItem);
        yield put(pdApiActions.setAPMFaults(apmFaults));
        const faults = updateArray(pdApi.faults, updatedItem);
        yield put(pdApiActions.setFaults(faults));
        break;
      }      
      case "signalizationFaults": {
        const signalizationFaults = updateArray(pdApi.signalizationFaults ?? [], updatedItem);
        yield put(pdApiActions.setSignalizationFaults(signalizationFaults));
        const faults = updateArray(pdApi.faults, updatedItem);
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "doorParameters": {
        const doorParameters = updateArray(pdApi.doorParameters, updatedItem);
        yield put(pdApiActions.setDoorParameters(doorParameters));
        break;
      }
      case "faultComponents": {
        const faultComponents = updateArray(pdApi.faultComponents, updatedItem);
        yield put(pdApiActions.setFaultComponents(faultComponents));
        break;
      }
      case "domains": {

        const updatedDomains = pdApi.domains[updatedItem.id].electrifications as Api.Electrification[];

        if (updatedDomains.length > updatedItem.electrifications.length) {
          const removedItem = pdApi.domains[updatedItem.id].electrifications?.filter(x => !updatedItem.electrifications.map((item: Api.Electrification) => item.enumValue).includes(x.enumValue));
          const index = (removedItem) ? pdApi.electrifications.findIndex(electrification => electrification.enumValue === removedItem[0].enumValue) : -1;
          if (index !== -1) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const pdApiElectrifications = pdApi.electrifications as any;
            pdApi.electrifications[index].domains = pdApiElectrifications[index].domains.filter((x: Api.Electrification) => x.id !== updatedItem.id);
          }

        }
        const enumValue = updatedItem.electrifications.map((item: Api.Electrification) => item.enumValue);
        const domain = _.omit(updatedItem, "electrifications");
        enumValue.forEach((item: number) => {
          const index = pdApi.electrifications.findIndex(electrification => electrification.enumValue === item);
          pdApi.electrifications[index].domains = [];
          pdApi.electrifications[index].domains?.push(domain as Api.Domain);
        });

        const domains = updateArray(_.values(pdApi.domains), updatedItem);
        yield put(pdApiActions.setDomains(domains));
        break;
      }
      case "parameterCategories": {
        const parameterCategories = updateArray(_.values(pdApi.parameterCategories), updatedItem);
        yield put(pdApiActions.setParameterCategories(parameterCategories));
        break;
      }
      case "kceVersions": {
        const kceVersions = updateArray(_.values(pdApi.kceVersions), updatedItem);
        yield put(pdApiActions.setKceVersions(kceVersions));
        break;
      }
      case "driveVersions": {
        const driveVersions = updateArray(_.values(pdApi.driveVersions), updatedItem);
        yield put(pdApiActions.setDriveVersions(driveVersions));
        break;
      }
      case "kceProducts": {
        const kceProducts = updateArray(_.values(pdApi.kceProducts), updatedItem);
        yield put(pdApiActions.setKceProducts(kceProducts));
        break;
      }
      case "signals": {
        const signals = updateArray(_.values(pdApi.signals), updatedItem);
        yield put(pdApiActions.setDriveSignals(signals));
        break;
      }
      case "driveTests": {
        const driveTests = updateArray(_.values(pdApi.driveTests), updatedItem);
        yield put(pdApiActions.setDriveTests(driveTests));
        break;
      }
      case "signalGroups": {
        const signalGroups = updateArray(_.values(pdApi.signalGroups), updatedItem);
        yield put(pdApiActions.setDriveSignalGroups(signalGroups));
        break;
      }
      case "escalatorRTDs": {
        const escalatorRTDs = updateArray(_.values(pdApi.escalatorRTDs), updatedItem);
        yield put(pdApiActions.setEscalatorRTDs(escalatorRTDs));
        break;
      }
      case "escalatorRTDGroups": {
        const escalatorRTDGroups = updateArray(_.values(pdApi.escalatorRTDGroups), updatedItem);
        yield put(pdApiActions.setEscalatorRTDGroups(escalatorRTDGroups));
        break;
      }
      case "escalatorEventLogs": {
        const escalatorEventLogs = updateArray(_.values(pdApi.escalatorEventLogs), updatedItem);
        yield put(pdApiActions.setEscalatorEventLogs(escalatorEventLogs));
        break;
      }
      case "users": {
        const users = updateArray(pdApi.users, updatedItem);
        yield put(pdApiActions.setUsers(users));
        break;
      }
      case "eventClasses": {
        const eventClasses = updateArray(pdApi.eventClasses, updatedItem);
        yield put(pdApiActions.setEventClasses(eventClasses));
        break;
      }
      case "eventTypes": {
        const eventTypes = updateArray(pdApi.eventTypes, updatedItem);
        yield put(pdApiActions.setEventTypes(eventTypes));
        break;
      }
      case "electrifications": {
        const electrifications = updateArray(pdApi.electrifications, updatedItem);
        yield put(pdApiActions.setElectrifications(electrifications));
        break;
      }
      case "eventPids": {
        const eventPids = updateArray(pdApi.eventPids, updatedItem);
        yield put(pdApiActions.setEventPids(eventPids));
        break;
      }
      case "extCommands": {
        const extCommands = updateArray(pdApi.extCommands, updatedItem);
        yield put(pdApiActions.setExtCommands(extCommands));
        break;
      }
      case "messageTypes": {
        const messageTypes = updateArray(pdApi.messageTypes, updatedItem);
        yield put(pdApiActions.setMessageTypes(messageTypes));
        break;
      }
      case "ioBoardTypes": {
        const ioBoardTypes = updateArray(pdApi.ioBoardTypes, updatedItem);
        yield put(pdApiActions.setIOBoardTypes(ioBoardTypes));
        break;
      }
      case "ioCpuEnums": {
        const ioCpuEnums = updateArray(pdApi.ioCpuEnums, updatedItem);
        yield put(pdApiActions.setIOCpus(ioCpuEnums));
        break;
      }
      case "ioNetworks": {
        const ioNetworks = updateArray(pdApi.ioNetworks, updatedItem);
        yield put(pdApiActions.setIONetwork(ioNetworks));
        break;
      }
      case "ioOptions": {
        const ioOptions = updateArray(pdApi.ioOptions, updatedItem);
        yield put(pdApiActions.setIOOptions(ioOptions));
        break;
      }
      case "ioDataTypes": {
        const ioDataTypes = updateArray(pdApi.ioDataTypes, updatedItem);
        yield put(pdApiActions.setIODatatypes(ioDataTypes));
        break;
      }
      case "ioBasicDataTypes": {
        const ioBasicDataTypes = updateArray(pdApi.ioBasicDataTypes, updatedItem);
        yield put(pdApiActions.setIOBasicDatatypes(ioBasicDataTypes));
        break;
      }
      case "iodataSections": {
        const iodataSections = updateArray(pdApi.iodataSections, updatedItem);
        yield put(pdApiActions.setIODataSections(iodataSections));
        break;
      }
      case "ioOptionsEnumType": {
        const ioOptionsEnumType = updateArray(pdApi.ioOptionsEnumType, updatedItem);
        yield put(pdApiActions.setIOEnumOptionsType(ioOptionsEnumType));
        break;
      }
      case "ioConfigurations":
        if (ioConfig.ioConfigurations) {
          const ioConfigurations = updateArray(ioConfig.ioConfigurations, updatedItem);
          yield put(ioConfigActions.setIoConfigurations(ioConfigurations));
        }
        break;
      case "standardBoards": {
        const standardBoards = updateArray(ioConfig.standardBoards, updatedItem);
        yield put(ioConfigActions.setStandardBoards(standardBoards));
        break;
      }

      case "standardNodes": {
        // eslint-disable-next-line no-lone-blocks
        const standardBoards = updateStandardBoardArrayWithNode(
          ioConfig.standardBoards,
          updatedItem
        );
        yield put(ioConfigActions.setStandardBoards(standardBoards));
        break;
      }

      case "standardActions": {
        const updatedPoints = updateStandardPointWithAction(ioConfig.standardPoints, updatedItem);
        yield put(ioConfigActions.setStandardPoints(updatedPoints));
        break;
      }
      case "ionodePointActions": {
        const updatedPoints = updateIoPointWithAction(ioConfig.ionodePoints, updatedItem);
        yield put(ioConfigActions.updateIoNodePoints(updatedPoints));
        break;
      }
      case "standardPoints": {
        const updatedPoints = updateArray(ioConfig.standardPoints, updatedItem);
        yield put(ioConfigActions.setStandardPoints(updatedPoints));
        break;
      }
      case "ionodePoints": {
        const updatedPoints = updateArray(ioConfig.ionodePoints, updatedItem);
        yield put(ioConfigActions.updateIoNodePoints(updatedPoints));
        break;
      }
      case "aadgroups": {
        const aadgroups = updateArray(pdApi.aadgroups, updatedItem);
        yield put(pdApiActions.setAADGroups(aadgroups));
        break;
      }
      case "localizationids": {
        const locales = updateArray(pdApi.locales, updatedItem);
        yield put(pdApiActions.setLocales(locales));
        break;
      }
      case "enumerations": {
        const enumerations = updateArray(_.values(pdApi.enumerations), updatedItem);
        yield put(pdApiActions.setCommonEnumerations(enumerations));
        break;
      }
      case "enumerationItems": {
        const enumerationItems = updateArray(_.values(pdApi.enumerationItems), updatedItem);
        yield put(pdApiActions.setCommonEnumerationItems(enumerationItems));
        // we also need to fetch enumerations to update the in-memory data
        const updatedEnumerations = yield call(pdApiGet, "enumerations");
        yield put(pdApiActions.setCommonEnumerations(updatedEnumerations));
        break;
      }
      case "doorEventClasses": {
        const doorEventClasses = updateArray(pdApi.doorEventClasses, updatedItem);
        yield put(pdApiActions.setDoorEventClasses(doorEventClasses));
        break;
      }
      case "doorEventTypes": {
        const doorEventTypes = updateArray(pdApi.doorEventTypes, updatedItem);
        yield put(pdApiActions.setDoorEventTypes(doorEventTypes));
        break;
      }
      case "doorEventPids": {
        const doorEventPids = updateArray(pdApi.doorEventPids, updatedItem);
        yield put(pdApiActions.setDoorEventPids(doorEventPids));
        break;
      }
      case "doorExtCommands": {
        const doorExtCommands = updateArray(pdApi.doorExtCommands, updatedItem);
        yield put(pdApiActions.setDoorExtCommands(doorExtCommands));
        break;
      }
      case "EscalatorEventClasses": {
        const EscalatorEventClasses = updateArray(pdApi.EscalatorEventClasses ?? [], updatedItem);
        yield put(pdApiActions.setEscalatorEventClasses(EscalatorEventClasses));
        break;
      }
      case "EscalatorEventTypes": {
        const EscalatorEventTypes = updateArray(pdApi.EscalatorEventTypes ?? [], updatedItem);
        yield put(pdApiActions.setEscalatorEventTypes(EscalatorEventTypes));
        break;
      }
      case "EscalatorEventPids": {
        const EscalatorEventPids = updateArray(pdApi.EscalatorEventPids ?? [], updatedItem);
        yield put(pdApiActions.setEscalatorEventPids(EscalatorEventPids));
        break;
      }
      case "EscalatorExtCommands": {
        const EscalatorExtCommands = updateArray(pdApi.EscalatorExtCommands ?? [], updatedItem);
        yield put(pdApiActions.setEscalatorExtCommand(EscalatorExtCommands));
        break;
      }
      case "EscalatorRequestTypes": {
        const EscalatorRequestTypes = updateArray(pdApi.EscalatorRequestTypes ?? [], updatedItem);
        yield put(pdApiActions.setEscalatorRequestTypes(EscalatorRequestTypes));
        break;
      }
      case "EscalatorVersions": {
        const escalatorVersions = updateArray(_.values(pdApi.EscalatorVersions), updatedItem);
        yield put(pdApiActions.setEscalatorVersions(escalatorVersions));
        break;
      }
      case "DoorVersions": {
        const DoorVersions = updateArray(pdApi.DoorVersions ?? [], updatedItem);
        yield put(pdApiActions.setDoorVersions(DoorVersions));
        break;
      }
      case "dataActs": {
        const dataActCodes = updateArray(pdApi.dataActCodes ?? [], updatedItem);
        yield put(pdApiActions.setDataActCodes(dataActCodes));
        break;
      }
      case "monitoringFaultCategories": {
        const monitoringFaultCategories = updateArray(pdApi.monitoringFaultCategories ?? [], updatedItem);
        yield put(pdApiActions.setMonitoringFaultCategories(monitoringFaultCategories));
        break;
      }
      case "escalatorDataActs": {
        const escalatorDataActs = updateArray(pdApi.escalatorDataActCodes ?? [], updatedItem);
        yield put(pdApiActions.setEscalatorDataActCodes(escalatorDataActs));
        break;
      }
      case "grandNorms": {
        const grandNorms = updateArray(pdApi.grandNorms ?? [], updatedItem);
        yield put(pdApiActions.setGrandNorms(grandNorms));
        break;
      }
      default:
        console.error("Something went wrong in update saga");
    }
    yield put(showSuccess(`UPDATED: ${noticationMessages[option](updatedItem)}`));
  } catch (e) {

    if (!e.response?.data?.status)//system error
    {
      yield put(showError(`UPDATE FAILED: ${noticationMessages[option](editedItem)}`, { autoDismiss: 5 }));
    } else if (e.response?.data?.status) {  //backend framed error
      if (e.response.data.status >= 400 && e.response.data.status < 500 && e.response.data.error?.length > 0) {
        yield put(showError(`${e.response.data.error}`, { autoDismiss: 10 }));
      } else {
        yield put(showError(`UPDATE FAILED: ${noticationMessages[option](editedItem)}`, { autoDismiss: 5 }));
      }
    }
  } finally {
    if (option === "standardActions" || option === "ionodePointActions") {
      yield put(ioConfigActions.setLocalFetchingStatus(undefined));
      yield put(editActions.setStored(editedItem));
    } else {
      yield put(editActions.setEdited(undefined));
      yield put(editActions.setStored(undefined));
    }
    yield put(
      mainActions.setLastEditedObjectId(
        editedItem.parentCategory?.id ??
        editedItem.id ??
        editedItem.boardTypeId ??
        editedItem.enumValue ??
        editedItem.value ??
        editedItem.locale,
        parentItem
      )
    );
    yield put(editActions.clearHasEdited());
    yield put(mainActions.setFetchingStatus(undefined));
  }
}

/** Update several items
 * TODO: common version
 */
export function* updateParametersSaga(
  action: ReturnType<typeof editActions.updateParameters>
): Api.AnyItem {
  const option = action.payload.parameterType;
  if (option !== "Lift" && option !== "Drive") {
    yield put(showError(`Invalid parameter type: ${option})`));
    return null;
  }
  const pdApi: State.PdApi = yield select(pdApiSelectors.pdApi);
  const parametersToUpdate: Api.AnyParameter[] = action.payload.parameters;
  let updateIndex = 1;
  let currentParameter = parametersToUpdate[0];

  yield put(mainActions.setFetchingStatus("Updating parameters"));
  try {
    for (const parameterToUpdate of parametersToUpdate) {
      const mediaType = getMediaType("parameters", parameterToUpdate, pdApi);
      currentParameter = parameterToUpdate;
      yield call(pdApiPut, "parameters", parameterToUpdate, mediaType);
      yield put(
        mainActions.setFetchingStatus(
          `Updating parameters ${((updateIndex / parametersToUpdate.length) * 100).toFixed(2)} %`
        )
      );
      updateIndex++;
    }
    yield put(showSuccess(`Parameters (count ${parametersToUpdate.length}) updated`));
  } catch (e) {
    if (!e.response?.data?.status)//system error
    {
      currentParameter.commonExtra
        ? yield put(
          showError(
            `Updating Parameter Domain ${currentParameter.commonExtra.domainName} Code ${currentParameter.code} failed. ${e}`,
            { autoDismiss: 5 }
          )
        )
        : yield put(showError(`Updating Parameter Code ${currentParameter.code} failed. ${e}`, { autoDismiss: 5 }));
    } else if (e.response?.data?.status) {  //backend framed error
      if (e.response.data.status >= 400 && e.response.data.status < 500 && e.response.data.error?.length > 0) {
        yield put(showError(`${e.response.data.error}`, { autoDismiss: 10 }));
      } else {
        currentParameter.commonExtra
          ? yield put(
            showError(
              `Updating Parameter Domain ${currentParameter.commonExtra.domainName} Code ${currentParameter.code} failed. ${e}`,
              { autoDismiss: 5 }
            )
          )
          : yield put(showError(`Updating Parameter Code ${currentParameter.code} failed. ${e}`, { autoDismiss: 5 }));
      }
    }
  } finally {
    yield put(mainActions.setFetchingStatus("Syncing parameters..."));
    yield put(pdApiActions.setParametersLoadingStatus(true));
    yield call(fetchParameter, option);
    yield put(pdApiActions.setParametersLoadingStatus(false));
    yield put(mainActions.setFetchingStatus(undefined));
  }
}


export function* updateDoorBindingsSaga(
  action: ReturnType<typeof editActions.updateDoorBindings>
): Api.AnyItem {
  yield put(mainActions.setFetchingStatus("Updating parameters"));
  try {
    if (action.payload.parameterType === "Remove") {
      yield call(pdApiPostBindings, "doorParameters", "removeBindingList", action.payload.parameters);
    } else {
      yield call(pdApiPostBindings, "doorParameters", "addBindingList", action.payload.parameters);
    }
    yield put(showSuccess(`Parameters (count ${action.payload.parameters.ParameterIdList.length}) updated`));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(mainActions.setFetchingStatus("Syncing parameters..."));
    yield call(fetchParameter, "Door");
    yield put(mainActions.setFetchingStatus(undefined));
  }
}

export function* updateLiftBindingsSaga(
  action: ReturnType<typeof editActions.updateLiftBindings>
): Api.AnyItem {
  yield put(mainActions.setFetchingStatus("Updating parameters"));
  try {
    if (action.payload.operation === "Remove") {
      yield call(pdApiPostBindings, "liftParameters", "removeBindingList", action.payload.liftBulkBindings);
    } else {
      yield call(pdApiPostBindings, "liftParameters", "addBindingList", action.payload.liftBulkBindings);
    }
    yield put(showSuccess(`Parameters (count ${action.payload.liftBulkBindings.parameterIdList.length}) updated`));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(mainActions.setFetchingStatus("Syncing parameters..."));
    yield call(fetchParameter, "Lift");
    yield put(mainActions.setFetchingStatus(undefined));
  }
}

export function* updateEscalatorBindingsSaga(
  action: ReturnType<typeof editActions.updateEscalatorBindings>
): Api.AnyItem {
  yield put(mainActions.setFetchingStatus("Updating parameters"));
  try {
    if (action.payload.operation === "Remove") {
      yield call(pdApiPostBindings, "escalatorParameters", "removeBindingList", action.payload.escalatorBulkBindings);
    } else {
      yield call(pdApiPostBindings, "escalatorParameters", "addBindingList", action.payload.escalatorBulkBindings);
    }
    yield put(showSuccess(`Parameters (count ${action.payload.escalatorBulkBindings.parameterIdList.length}) updated`));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(mainActions.setFetchingStatus("Syncing parameters..."));
    yield call(fetchEscalatorParameters);
    yield put(mainActions.setFetchingStatus(undefined));
  }
}

export function* updateEscalatorFaultBindingsSaga(
  action: ReturnType<typeof editActions.updateEscalatorFaultBindings>
): Api.AnyItem {
  yield put(mainActions.setFetchingStatus("Updating faults"));
  try {
    if (action.payload.operation === "Remove") {
      yield call(pdApiPostBindings, "escalatorFaults", "removeBindingList", action.payload.escalatorFaultBulkBindings);
    } else {
      yield call(pdApiPostBindings, "escalatorFaults", "addBindingList", action.payload.escalatorFaultBulkBindings);
    }
    yield put(showSuccess(`Faults (count ${action.payload.escalatorFaultBulkBindings.faultIdList.length}) updated`));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(mainActions.setFetchingStatus("Syncing faults..."));
    yield call(fetchEscalatorFaults);
    yield put(mainActions.setFetchingStatus(undefined));
  }
}

export function* addActionSaga(action: ReturnType<typeof editActions.addAction>): Api.AnyItem {
  const parentPointId = action.payload.parentPointId;
  const actionType = action.payload.actionType;
  const ioConfig: State.IoConfig = yield select(ioConfigSelectors.ioConfig);
  const NewAction = createEmptyStandardAction(parentPointId);
  try {
    const addedStandardAction = yield call(pdApiPost, actionType, NewAction);
    if (actionType === "ionodePointActions") {
      const updatedIonodePoints = addActionToIonodePoints(
        ioConfig.ionodePoints,
        addedStandardAction
      );
      yield put(ioConfigActions.updateIoNodePoints(updatedIonodePoints));
    } else {
      const updatedStandardPoints = addActionToStandardPoints(
        ioConfig.standardPoints,
        addedStandardAction
      );
      yield put(ioConfigActions.setStandardPoints(updatedStandardPoints));
    }
  } catch (e) {
    if (!e.response?.data?.status)//system error
    {
      yield put(showError("ADD FAILED: Action", { autoDismiss: 5 }));
    } else if (e.response?.data?.status) {  //backend framed error
      if (e.response.data.status >= 400 && e.response.data.status < 500 && e.response.data.error?.length > 0) {
        yield put(showError(`${e.response.data.error}`, { autoDismiss: 10 }));
      } else {
        yield put(showError("ADD FAILED: Action", { autoDismiss: 5 }));
      }
    }
  }
}

export function* addNodeSaga(action: ReturnType<typeof editActions.addNode>): Api.AnyItem {
  const parentId = action.payload;
  const ioConfig: State.IoConfig = yield select(ioConfigSelectors.ioConfig);
  const newItem = createEmptyStandardNode(parentId);

  try {
    const addedItem = yield call(pdApiPost, "standardNodes", newItem);
    const updatedStandardBoards = addNodeToStandardBoards(ioConfig.standardBoards, addedItem);
    yield put(ioConfigActions.setStandardBoards(updatedStandardBoards));
    yield put(editActions.setEdited(updatedStandardBoards.find(board => board.id === parentId)));
  } catch (e) {
    if (!e.response?.data?.status)//system error
    {
      yield put(showError("ADD FAILED: Node", { autoDismiss: 5 }));
    } else if (e.response?.data?.status) {  //backend framed error
      if (e.response.data.status >= 400 && e.response.data.status < 500 && e.response.data.error?.length > 0) {
        yield put(showError(`${e.response.data.error}`, { autoDismiss: 10 }));
      } else {
        yield put(showError("ADD FAILED: Node", { autoDismiss: 5 }));
      }
    }
  }
}

export function* addTranslationsSaga(
  action: ReturnType<typeof editActions.addTranslations>
): Api.AnyItem {
  const translations = action.payload;

  yield put(mainActions.setFetchingStatus("Updating translations"));
  try {
    const updatedTranslations: Api.TranslationResponse = yield call(
      pdApiPost,
      "translations",
      translations
    );

    const translatedCount = updatedTranslations.reportItems.reduce(
      (acc, currentValue) => acc + currentValue.translatedCount,
      0
    );
    const notTranslatedCount = updatedTranslations.reportItems.reduce(
      (acc, currentValue) => acc + currentValue.untranslatedCount,
      0
    );

    const totalCount = translatedCount + notTranslatedCount;
    const translatedPercent = ((translatedCount / totalCount) * 100).toFixed(2);

    yield put(
      showSuccess(
        `ADDED: Translations for locale: ${updatedTranslations.locale}. Updated ${updatedTranslations.affectedRows} rows for ${updatedTranslations.type}.
          There are ${translatedCount} translated items and ${notTranslatedCount} non-translated.
          A total of ${translatedPercent}% of the texts have been translated.`,
        { id: "DeliveringTranslatedTexts", autoDismiss: 10 }
      )
    );
  } catch (e) {
    //yield put(showError("Oops something went wrong when updating the translations"));
    if (!e.response?.data?.status)//system error
    {
      yield put(showError("Oops something went wrong when updating the translations", { autoDismiss: 10 }));
    } else if (e.response?.data?.status) {  //backend framed error
      if (e.response.data.status >= 400 && e.response.data.status < 500 && e.response.data.error?.length > 0) {
        yield put(showError(`${e.response.data.error}`, { autoDismiss: 10 }));
      } else {
        yield put(showError("Oops something went wrong when updating the translations", { autoDismiss: 10 }));
      }
    }
  } finally {
    yield put(mainActions.setFetchingStatus(undefined));
  }
}

/** Add item */
export function* addSaga(action: ReturnType<typeof editActions.add>): Api.AnyItem {
  const option = action.payload;
  const queryOption = action.payload === "escalatorParameters"
    ? "escalatorParameters"
    : action.payload === "doorParameters"
      ? "doorParameters"
      : action.payload.includes("Parameter")
        ? "parameters" : action.payload === "apmFaults"
          ? "faults" : (action.payload === "driveFaults")
            ? "faults" : (action.payload === "liftFaults")
              ? "faults" : (action.payload === "groupFaults")
                ? "faults" : (action.payload === "signalizationFaults")
                  ? "faults" : action.payload;
  const pdApi: State.PdApi = yield select(pdApiSelectors.pdApi);
  const ioConfig: State.IoConfig = yield select(ioConfigSelectors.ioConfig);
  const editedItem = yield select(editViewSelectors.editedObject);
  let createNotification, addedItem;
  if (option === "ioBasicDataTypes") {
    editedItem.iodataSections = _.map(editedItem.iodataSections, item => ({ id: item.id }));
  }

  if (option === "ioBoards" && Object.hasOwn(editedItem, "modelId") && editedItem.ionodes.length !== 0) {
    const boardId = editedItem.ionodes[0].boardId;
    const ioPoints = ioConfig.ionodePoints.filter(item => boardId === item.boardId);

    if (ioPoints.length > 0) {
      editedItem.ionodes.forEach((item: Api.IoNode) => {
        ioPoints.forEach(sub => {
          if (item.id === sub.nodeId) {
            item.ionodePoints.push(sub);
          }
        });
      });
    }
  }

  if (option === "standardActions") {
    yield put(ioConfigActions.setLocalFetchingStatus("Updating"));
  }
  else if (option === "ioConfigurations") {
    if ("ioboards" in editedItem) {
      createNotification = yield put(
        showInfo("Copying IO configuration \"" + editedItem.friendlyName + "\". This may take a few minutes.", { id: "CopyIOConfigurationNotification", autoDismiss: 0 })
      );
    }
    else {
      createNotification = yield put(
        showInfo("Creating IO configuration \"" + editedItem.friendlyName + "\". This may take a few minutes.", { id: "CopyIOConfigurationNotification", autoDismiss: 0 })
      );
    }
    yield put(editActions.setEdited(undefined));
  }
  else {
    yield put(mainActions.setFetchingStatus("Creating"));
  }
  try {
    const mediaType = getMediaType(queryOption, editedItem, pdApi);
    addedItem = yield call(pdApiPost, queryOption, editedItem, mediaType);
    switch (option) {
      case "faults": {
        const faults = [...pdApi.faults, addedItem];
        yield put(pdApiActions.setFaults(faults));
        const driveFaults = [...(pdApi.driveFaults ?? []), addedItem];
        yield put(pdApiActions.setDriveFaults(driveFaults));
        const liftFaults = [...(pdApi.liftFaults ?? []), addedItem];
        yield put(pdApiActions.setLiftFaults(liftFaults));
        const groupFaults = [...(pdApi.groupFaults ?? []), addedItem];
        yield put(pdApiActions.setGroupFaults(groupFaults));
        break;
      }
      case "driveFaults": {
        const driveFaults = [...(pdApi.driveFaults ?? []), addedItem];
        yield put(pdApiActions.setDriveFaults(driveFaults));
        const faults = [...pdApi.faults, addedItem];
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "liftFaults": {
        const liftFaults = [...(pdApi.liftFaults ?? []), addedItem];
        yield put(pdApiActions.setLiftFaults(liftFaults));
        const faults = [...pdApi.faults, addedItem];
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "groupFaults": {
        const groupFaults = [...(pdApi.groupFaults ?? []), addedItem];
        yield put(pdApiActions.setGroupFaults(groupFaults));
        const faults = [...pdApi.faults, addedItem];
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "networkFaults": {
        const networkFaults = [...(pdApi.networkFaults ?? []), addedItem];
        yield put(pdApiActions.setNetworkFaults(networkFaults));
        const faults = [...pdApi.faults, addedItem];
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "monitoringFaults": {
        const monitoringFaults = [...(pdApi.monitoringFaults ?? []), addedItem];
        yield put(pdApiActions.setMonitoringFaults(monitoringFaults));
        const faults2 = [...pdApi.faults, addedItem];
        yield put(pdApiActions.setFaults(faults2));
        break;
      }
      case "escalatorFaults": {
        const escalatorFaults = [...pdApi.escalatorFaults, addedItem];
        yield put(pdApiActions.setEscalatorFaults(escalatorFaults));
        break;
      }
      case "doorFaults": {
        const doorFaults = [...pdApi.doorFaults, addedItem];
        yield put(pdApiActions.setDoorFaults(doorFaults));
        break;
      }
      case "nonKoneFaults": {
        const nonKoneFaults = [...pdApi.nonKoneFaults, addedItem];
        yield put(pdApiActions.setNonKoneFaults(nonKoneFaults));
        break;
      }
      case "liftParameters": {
        const liftParameters = [...pdApi.liftParameters, addedItem];
        yield put(pdApiActions.setLiftParameters(liftParameters));
        break;
      }
      case "driveParameters": {
        const driveParameters = [...(pdApi.driveParameters ?? []), addedItem];
        yield put(pdApiActions.setDriveParameters(driveParameters));
        break;
      }
      case "groupParameters": {
        const groupParameters = [...pdApi.groupParameters, addedItem];
        yield put(pdApiActions.setGroupParameters(groupParameters));
        break;
      }
      case "safetyParameters": {
        const safetyParameters = [...pdApi.safetyParameters, addedItem];
        yield put(pdApiActions.setSafetyParameters(safetyParameters));
        break;
      }
      case "escalatorParameters": {
        const escalatorParameters = [...pdApi.escalatorParameters, addedItem];
        yield put(pdApiActions.setEscalatorParameters(escalatorParameters));
        break;
      }
      case "doorParameters": {
        const doorParameters = [...pdApi.doorParameters, addedItem];
        yield put(pdApiActions.setDoorParameters(doorParameters));
        break;
      }
      case "apmParameters": {
        const apmParameters = [...(pdApi.apmParameters ?? []), addedItem];
        yield put(pdApiActions.setAPMParameters(apmParameters));
        break;
      }
      case "apmFaults": {
        const apmFaults = [...(pdApi.apmFaults ?? []), addedItem];
        const faults = [...pdApi.faults, addedItem];
        yield put(pdApiActions.setFaults(faults));
        yield put(pdApiActions.setAPMFaults(apmFaults));
        break;
      }
      case "signalizationFaults": {
        const signalizationFaults = [...(pdApi.signalizationFaults ?? []), addedItem];
        const faults = [...pdApi.faults, addedItem];
        yield put(pdApiActions.setFaults(faults));
        yield put(pdApiActions.setSignalizationFaults(signalizationFaults));
        break;
      }
      case "faultComponents": {
        const faultComponents = [...pdApi.faultComponents, addedItem];
        yield put(pdApiActions.setFaultComponents(faultComponents));
        break;
      }
      case "domains": {
        const enumValue = addedItem.electrifications.map((item: Api.Electrification) => item.enumValue);
        const domain = _.omit(addedItem, "electrifications");
        enumValue.forEach((item: number) => {
          const index = pdApi.electrifications.findIndex(electrification => electrification.enumValue === item);
          pdApi.electrifications[index].domains = [];
          pdApi.electrifications[index].domains?.push(domain as Api.Domain);
        });
        const domains = [..._.values(pdApi.domains), addedItem];
        yield put(pdApiActions.setDomains(domains));
        break;
      }
      case "parameterCategories": {
        const parameterCategories = [..._.values(pdApi.parameterCategories), addedItem];
        yield put(pdApiActions.setParameterCategories(parameterCategories));
        break;
      }
      case "kceProducts": {
        const kceProducts = [..._.values(pdApi.kceProducts), addedItem];
        yield put(pdApiActions.setKceProducts(kceProducts));
        break;
      }
      case "kceVersions": {
        const kceVersions = [..._.values(pdApi.kceVersions), addedItem];
        yield put(pdApiActions.setKceVersions(kceVersions));
        break;
      }
      case "driveVersions": {
        const driveVersions = [..._.values(pdApi.driveVersions), addedItem];
        yield put(pdApiActions.setDriveVersions(driveVersions));
        break;
      }
      case "signals": {
        const signals = [..._.values(pdApi.signals), addedItem];
        yield put(pdApiActions.setDriveSignals(signals));
        break;
      }
      case "driveTests": {
        const driveTests = [..._.values(pdApi.driveTests), addedItem];
        yield put(pdApiActions.setDriveTests(driveTests));
        break;
      }
      case "signalGroups": {
        const signalGroups = [..._.values(pdApi.signalGroups), addedItem];
        yield put(pdApiActions.setDriveSignalGroups(signalGroups));
        break;
      }
      case "escalatorRTDs": {
        const escalatorRTDs = [..._.values(pdApi.escalatorRTDs), addedItem];
        yield put(pdApiActions.setEscalatorRTDs(escalatorRTDs));
        break;
      }
      case "escalatorRTDGroups": {
        const escalatorRTDGroups = [..._.values(pdApi.escalatorRTDGroups), addedItem];
        yield put(pdApiActions.setEscalatorRTDGroups(escalatorRTDGroups));
        break;
      }
      case "escalatorEventLogs": {
        const escalatorEventLogs = [..._.values(pdApi.escalatorEventLogs), addedItem];
        yield put(pdApiActions.setEscalatorEventLogs(escalatorEventLogs));
        break;
      }
      case "users": {
        const users = [...pdApi.users, addedItem];
        yield put(pdApiActions.setUsers(users));
        break;
      }
      case "eventClasses": {
        const eventClasses = [..._.values(pdApi.eventClasses), addedItem].sort((a, b) => a.enumValue - b.enumValue);
        yield put(pdApiActions.setEventClasses(eventClasses));
        break;
      }
      case "eventTypes": {
        const eventTypes = [..._.values(pdApi.eventTypes), addedItem].sort((a, b) => a.enumValue - b.enumValue);
        yield put(pdApiActions.setEventTypes(eventTypes));
        break;
      }
      case "electrifications": {
        const electrifications = [..._.values(pdApi.electrifications), addedItem];
        yield put(pdApiActions.setElectrifications(electrifications));
        break;
      }
      case "eventPids": {
        const eventPids = [..._.values(pdApi.eventPids), addedItem].sort((a, b) => a.enumValue - b.enumValue);
        yield put(pdApiActions.setEventPids(eventPids));
        break;
      }
      case "extCommands": {
        const extCommands = [..._.values(pdApi.extCommands), addedItem].sort((a, b) => a.enumValue - b.enumValue);
        yield put(pdApiActions.setExtCommands(extCommands));
        break;
      }
      case "messageTypes": {
        const messageTypes = [..._.values(pdApi.messageTypes), addedItem].sort((a, b) => a.enumValue - b.enumValue);
        yield put(pdApiActions.setMessageTypes(messageTypes));
        break;
      }
      case "aadgroups": {
        const aadgroups = [..._.values(pdApi.aadgroups), addedItem];
        yield put(pdApiActions.setAADGroups(aadgroups));
        break;
      }
      case "localizationids": {
        const locales = [..._.values(pdApi.locales), addedItem];
        yield put(pdApiActions.setLocales(locales));
        break;
      }
      case "ioBoardTypes": {
        const ioBoardTypes = [..._.values(pdApi.ioBoardTypes), addedItem];
        yield put(pdApiActions.setIOBoardTypes(ioBoardTypes));
        break;
      }
      case "ioCpuEnums": {
        const ioCpuEnums = [..._.values(pdApi.ioCpuEnums), addedItem];
        yield put(pdApiActions.setIOCpus(ioCpuEnums));
        break;
      }
      case "ioNetworks": {
        const ioNetworks = [..._.values(pdApi.ioNetworks), addedItem];
        yield put(pdApiActions.setIONetwork(ioNetworks));
        break;
      }
      case "ioOptions": {
        const ioOptions = [..._.values(pdApi.ioOptions), addedItem];
        yield put(pdApiActions.setIOOptions(ioOptions));
        break;
      }
      case "ioOptionsEnumType": {
        const ioOptionsEnumType = [..._.values(pdApi.ioOptionsEnumType), addedItem];
        yield put(pdApiActions.setIOEnumOptionsType(ioOptionsEnumType));
        break;
      }
      case "ioDataTypes": {
        const ioDataTypes = [..._.values(pdApi.ioDataTypes), addedItem];
        yield put(pdApiActions.setIODatatypes(ioDataTypes));
        break;
      }
      case "ioBasicDataTypes": {
        const ioBasicDataTypes = [..._.values(pdApi.ioBasicDataTypes), addedItem];
        yield put(pdApiActions.setIOBasicDatatypes(ioBasicDataTypes));
        break;
      }
      case "iodataSections": {
        const iodataSections = [..._.values(pdApi.iodataSections), addedItem];
        yield put(pdApiActions.setIODataSections(iodataSections));
        break;
      }
      case "ioConfigurations": {
        const ioConfigurations = [..._.values(ioConfig.ioConfigurations), addedItem];
        yield put(ioConfigActions.setIoConfigurations(ioConfigurations));
        break;
      }
      case "standardBoards": {
        const standardBoards = [..._.values(ioConfig.standardBoards), addedItem];
        yield put(ioConfigActions.setStandardBoards(standardBoards));
        yield put(ioConfigActions.updateStandardPoints(addedItem));
        yield put(ioConfigActions.updateOpenedStandardBoards(addedItem.id));
        break;
      }

      case "standardPoints": {
        const standardPoints = [..._.values(ioConfig.standardPoints), addedItem];
        yield put(ioConfigActions.setStandardPoints(standardPoints));
        break;
      }
      case "ionodePoints": {
        const ionodePoints = [..._.values(ioConfig.ionodePoints), addedItem];
        yield put(ioConfigActions.updateIoNodePoints(ionodePoints));
        break;
      }
      case "ioBoards": {
        const addedItemBoardType = addedItem.standard.boardType.enumName;
        const addedItemRevision = addedItem.standard.revision;
        const groupedItem = { [addedItemBoardType]: { [addedItemRevision]: addedItem } };
        const ioBoardsGrouped = [..._.values(ioConfig.ioBoardsGrouped), groupedItem];
        yield put(ioConfigActions.setIoBoardsGrouped(ioBoardsGrouped));
        break;
      }
      case "standardNodes": {
        const standardBoards = updateStandardBoardArrayWithNode(ioConfig.standardBoards, addedItem);
        yield put(ioConfigActions.setStandardBoards(standardBoards));
        break;
      }

      case "enumerations": {
        const enumerations = [..._.values(pdApi.enumerations), addedItem];
        yield put(pdApiActions.setCommonEnumerations(enumerations));
        yield put(editActions.selectParent(addedItem));
        break;
      }
      case "enumerationItems": {
        const enumerationItems = [..._.values(pdApi.enumerationItems), addedItem];
        yield put(pdApiActions.setCommonEnumerationItems(enumerationItems));
        // we also need to fetch enumerations to update the in-memory data
        const updatedEnumerations = yield call(pdApiGet, "enumerations");
        yield put(pdApiActions.setCommonEnumerations(updatedEnumerations));
        break;
      }
      case "doorEventClasses": {
        const doorEventClasses = [..._.values(pdApi.doorEventClasses), addedItem];
        yield put(pdApiActions.setDoorEventClasses(doorEventClasses));
        break;
      }
      case "doorEventTypes": {
        const doorEventTypes = [..._.values(pdApi.doorEventTypes), addedItem];
        yield put(pdApiActions.setDoorEventTypes(doorEventTypes));
        break;
      }
      case "doorEventPids": {
        const doorEventPids = [..._.values(pdApi.doorEventPids), addedItem];
        yield put(pdApiActions.setDoorEventPids(doorEventPids));
        break;
      }
      case "doorExtCommands": {
        const doorExtCommands = [..._.values(pdApi.doorExtCommands), addedItem];
        yield put(pdApiActions.setDoorExtCommands(doorExtCommands));
        break;
      }
      case "EscalatorEventClasses": {
        const EscalatorEventClasses = [..._.values(pdApi.EscalatorEventClasses), addedItem];
        yield put(pdApiActions.setEscalatorEventClasses(EscalatorEventClasses));
        break;
      }
      case "EscalatorEventTypes": {
        const EscalatorEventTypes = [..._.values(pdApi.EscalatorEventTypes), addedItem];
        yield put(pdApiActions.setEscalatorEventTypes(EscalatorEventTypes));
        break;
      }
      case "EscalatorEventPids": {
        const EscalatorEventPids = [..._.values(pdApi.EscalatorEventPids), addedItem];
        yield put(pdApiActions.setEscalatorEventPids(EscalatorEventPids));
        break;
      }
      case "EscalatorExtCommands": {
        const EscalatorExtCommands = [..._.values(pdApi.EscalatorExtCommands), addedItem];
        yield put(pdApiActions.setEscalatorExtCommand(EscalatorExtCommands));
        break;
      }
      case "EscalatorRequestTypes": {
        const EscalatorRequestTypes = [..._.values(pdApi.EscalatorRequestTypes), addedItem];
        yield put(pdApiActions.setEscalatorRequestTypes(EscalatorRequestTypes));
        break;
      }
      case "EscalatorVersions": {
        const escalatorVersions = [..._.values(pdApi.EscalatorVersions), addedItem];
        yield put(pdApiActions.setEscalatorVersions(escalatorVersions));
        break;
      }
      case "DoorVersions": {
        const DoorVersions = [..._.values(pdApi.DoorVersions), addedItem];
        yield put(pdApiActions.setDoorVersions(DoorVersions));
        break;
      }
      case "dataActs": {
        const dataActCodes = [...(pdApi.dataActCodes ?? []), addedItem];
        yield put(pdApiActions.setDataActCodes(dataActCodes));
        break;
      }
      case "monitoringFaultCategories": {
        const monitoringFaultCategories = [...(pdApi.monitoringFaultCategories ?? []), addedItem];
        yield put(pdApiActions.setMonitoringFaultCategories(monitoringFaultCategories));
        break;
      }
      case "escalatorDataActs": {
        const escalatorDataActs = [...(pdApi.escalatorDataActCodes ?? []), addedItem];
        yield put(pdApiActions.setEscalatorDataActCodes(escalatorDataActs));
        break;
      }
      case "grandNorms": {
        const grandNorms = [...(pdApi.grandNorms ?? []), addedItem];
        yield put(pdApiActions.setGrandNorms(grandNorms));
        break;
      }
      default:
        console.error("Something went wrong in add saga. Propably forgot to add switch case ");
    }
    if (option !== "ioConfigurations") {
      yield put(showSuccess(`ADDED: ${noticationMessages[option](addedItem)}`));
    }
    //Added item is set only for success not for error.
    if (option !== "standardActions" && option !== "ioConfigurations") {
      const addedItemId: string | number =
        addedItem.id ?? addedItem.boardTypeId ?? addedItem.enumValue ?? addedItem.value;
      const addedParentItemId: string | number = addedItem.parentId;

      yield put(mainActions.setLastEditedObjectId(addedItemId, addedParentItemId));
    }
  } catch (e) {
    if (!e.response?.data?.status)//system error
    {
      yield put(showError(`ADD FAILED: ${noticationMessages[option](editedItem)}`, { autoDismiss: 5 }));
    } else if (e.response?.data?.status) {  //backend framed error
      if (e.response.data.status >= 400 && e.response.data.status < 500 && e.response.data.error?.length > 0) {
        yield put(showError(`${e.response.data.error}`, { autoDismiss: 10 }));
      } else {
        yield put(showError(`ADD FAILED: ${noticationMessages[option](editedItem)}`, { autoDismiss: 5 }));
      }
    }
  } finally {
    if (option === "standardActions") {
      yield put(ioConfigActions.setLocalFetchingStatus(undefined));
      yield put(editActions.setStored(editedItem));
    } else if (option === "ioConfigurations") {
      yield put(hideNotification(createNotification.uid));
      if ("ioboards" in editedItem && addedItem) {
        yield put(showSuccess("IO configuration \"" + editedItem.friendlyName + "\" copied.",
          {
            id: "CopyIOConfigurationSuccessNotification",
            autoDismiss: 0,
            linkText: "View copy",
            linkUrl: `${window.location}#` + addedItem.id
          }));
      }
      else if (addedItem) {
        yield put(showSuccess("IO configuration \"" + editedItem.friendlyName + "\" created.",
          {
            id: "CopyIOConfigurationSuccessNotification",
            autoDismiss: 0,
            linkText: "View here",
            linkUrl: `${window.location}#` + addedItem.id
          }));
      }
      yield put(editActions.setStored(editedItem));
    }
    else {
      yield put(editActions.setEdited(undefined));
    }
    yield put(mainActions.setFetchingStatus(undefined));
  }
}

export function* deleteByIdSaga(action: ReturnType<typeof editActions.deleteById>): Api.AnyItem {
  const option = action.payload.option;
  const id = action.payload.id;
  const ioConfig: State.IoConfig = yield select(ioConfigSelectors.ioConfig);
  // yield put(mainActions.setFetchingStatus("Deleting"));
  try {
    yield call(pdApiDelete, option, id);
    switch (option) {
      case "standardNodes": {
        const standardBoards = ioConfig.standardBoards.map(board => {
          const updatedNodes = board.standardNodes?.filter(
            (node: any) => node.id !== id
          );
          return { ...board, standardNodes: updatedNodes ?? [] };
        });
        yield put(pdApiActions.setStandardBoards(standardBoards));
        // eslint-disable-next-line no-case-declarations
        const editedBoard = yield select(editViewSelectors.editedObject);
        yield put(editActions.setEdited(standardBoards.find(board => board.id === editedBoard.id)));
        break;
      }
      default:
        console.error("Something went wrong in delete saga");
    }
  } catch (e) {
    if (!e.response?.data?.status)//system error
    {
      yield put(showError("Error deleting item: :" + id + ", option: " + option, { autoDismiss: 5 }));
    } else if (e.response?.data?.status) {  //backend framed error
      if (e.response.data.status >= 400 && e.response.data.status < 500 && e.response.data.error?.length > 0) {
        yield put(showError(`${e.response.data.error}`, { autoDismiss: 10 }));
      } else {
        yield put(showError("Error deleting item: :" + id + ", option: " + option, { autoDismiss: 5 }));
      }
    }
  } finally {
    yield put(mainActions.setFetchingStatus(undefined));
  }
}

/** Delete item */
export function* deleteSaga(action: ReturnType<typeof editActions.delete>): Api.AnyItem {
  const option = action.payload;
  const queryOption = action.payload === "escalatorParameters"
    ? "escalatorParameters"
    : action.payload === "doorParameters"
      ? "doorParameters"
      : action.payload.includes("Parameter")
        ? "parameters" : action.payload === "apmFaults"
          ? "faults" : (action.payload === "driveFaults")
            ? "faults" : (action.payload === "liftFaults")
              ? "faults" : (action.payload === "groupFaults")
                ? "faults" : (action.payload === "signalizationFaults")
                  ? "faults" : action.payload;
  const pdApi: State.PdApi = yield select(pdApiSelectors.pdApi);
  const ioConfig: State.IoConfig = yield select(ioConfigSelectors.ioConfig);
  const editedItem = yield select(editViewSelectors.editedObject);
  const id = editedItem.id ?? editedItem.enumValue ?? editedItem.boardTypeId ?? editedItem.enumID;
  yield put(mainActions.setFetchingStatus("Deleting"));
  try {
    yield call(pdApiDelete, queryOption, id as number);
    switch (option) {
      case "faults": {
        const faults = pdApi.faults.filter(fault => fault.id !== id);
        yield put(pdApiActions.setFaults(faults));
        const driveFaults = (pdApi.driveFaults ?? []).filter(fault => fault.id !== id);
        yield put(pdApiActions.setDriveFaults(driveFaults));
        const liftFaults = (pdApi.liftFaults ?? []).filter(fault => fault.id !== id);
        yield put(pdApiActions.setLiftFaults(liftFaults));
        const groupFaults = (pdApi.groupFaults ?? []).filter(fault => fault.id !== id);
        yield put(pdApiActions.setGroupFaults(groupFaults));
        break;
      }
      case "driveFaults": {
        const driveFaults = (pdApi.driveFaults ?? []).filter(fault => fault.id !== id);
        yield put(pdApiActions.setDriveFaults(driveFaults));
        const faults = pdApi.faults.filter(fault => fault.id !== id);
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "liftFaults": {
        const liftFaults = (pdApi.liftFaults ?? []).filter(fault => fault.id !== id);
        yield put(pdApiActions.setLiftFaults(liftFaults));
        const faults = pdApi.faults.filter(fault => fault.id !== id);
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "groupFaults": {
        const groupFaults = (pdApi.groupFaults ?? []).filter(fault => fault.id !== id);
        yield put(pdApiActions.setGroupFaults(groupFaults));
        const faults = pdApi.faults.filter(fault => fault.id !== id);
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "networkFaults": {
        const networkFaults = (pdApi.networkFaults ?? []).filter(fault => fault.id !== id);
        yield put(pdApiActions.setNetworkFaults(networkFaults));
        const faults = pdApi.faults.filter(fault => fault.id !== id);
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "monitoringFaults": {
        const monitoringFaults = (pdApi.monitoringFaults ?? []).filter(fault => fault.id !== id);
        yield put(pdApiActions.setMonitoringFaults(monitoringFaults));
        const faults2 = pdApi.faults.filter(fault => fault.id !== id);
        yield put(pdApiActions.setFaults(faults2));
        break;
      }
      case "nonKoneFaults": {
        const nonKoneFaults = pdApi.nonKoneFaults.filter(fault => fault.id !== id);
        yield put(pdApiActions.setNonKoneFaults(nonKoneFaults));
        break;
      }
      case "escalatorFaults": {
        const escalatorFaults = pdApi.escalatorFaults.filter(fault => fault.id !== id);
        yield put(pdApiActions.setEscalatorFaults(escalatorFaults));
        break;
      }
      case "doorFaults": {
        const doorFaults = pdApi.doorFaults.filter(fault => fault.id !== id);
        yield put(pdApiActions.setDoorFaults(doorFaults));
        break;
      }
      case "apmFaults": {
        const apmFaults = (pdApi.apmFaults ?? []).filter(fault => fault.id !== id);
        yield put(pdApiActions.setAPMFaults(apmFaults));
        const faults = pdApi.faults.filter(fault => fault.id !== id);
        yield put(pdApiActions.setFaults(faults));
        break;
      }      
      case "signalizationFaults": {
        const signalizationFaults = (pdApi.signalizationFaults ?? []).filter(fault => fault.id !== id);
        yield put(pdApiActions.setSignalizationFaults(signalizationFaults));
        const faults = pdApi.faults.filter(fault => fault.id !== id);
        yield put(pdApiActions.setFaults(faults));
        break;
      }
      case "liftParameters": {
        const liftParameters = pdApi.liftParameters.filter(parameter => parameter.id !== id);
        yield put(pdApiActions.setLiftParameters(liftParameters));
        break;
      }
      case "groupParameters": {
        const groupParameters = pdApi.groupParameters.filter(parameter => parameter.id !== id);
        yield put(pdApiActions.setGroupParameters(groupParameters));
        break;
      }
      case "driveParameters": {
        const driveParameters = pdApi.driveParameters.filter(parameter => parameter.id !== id);
        yield put(pdApiActions.setDriveParameters(driveParameters));
        break;
      }
      case "safetyParameters": {
        const safetyParameters = pdApi.safetyParameters.filter(parameter => parameter.id !== id);
        yield put(pdApiActions.setSafetyParameters(safetyParameters));
        break;
      }
      case "escalatorParameters": {
        const escalatorParameters = pdApi.escalatorParameters.filter(parameter => parameter.id !== id);
        yield put(pdApiActions.setEscalatorParameters(escalatorParameters));
        break;
      }
      case "apmParameters": {
        const apmParameters = (pdApi.apmParameters ?? []).filter(parameter => parameter.id !== id);
        yield put(pdApiActions.setAPMParameters(apmParameters));
        break;
      }
      case "doorParameters": {
        const doorParameters = pdApi.doorParameters.filter(parameter => parameter.id !== id);
        yield put(pdApiActions.setDoorParameters(doorParameters));
        break;
      }
      case "faultComponents": {
        const faultComponents = pdApi.faultComponents.filter(
          faultComponent => faultComponent.id !== id
        );
        yield put(pdApiActions.setFaultComponents(faultComponents));
        break;
      }
      case "monitoringFaultCategories": {
        const monitoringFaultCategories = (pdApi.monitoringFaultCategories ?? []).filter(
          monitoringFaultCategory => monitoringFaultCategory.id !== id
        );
        yield put(pdApiActions.setMonitoringFaultCategories(monitoringFaultCategories));
        break;
      }
      case "domains": {
        const domains = _.values(pdApi.domains).filter(domain => domain.id !== id);
        yield put(pdApiActions.setDomains(domains));
        break;
      }
      case "parameterCategories": {
        const parameterCategories = _.values(pdApi.parameterCategories).filter(
          parameterCategory => parameterCategory?.id !== id
        );
        yield put(pdApiActions.setParameterCategories(parameterCategories));
        break;
      }
      case "kceProducts": {
        const kceProducts = _.values(pdApi.kceProducts).filter(kceProduct => kceProduct.id !== id);
        yield put(pdApiActions.setKceProducts(kceProducts));
        break;
      }
      case "kceVersions": {
        const kceVersions = _.values(pdApi.kceVersions).filter(kceVersion => kceVersion.id !== id);
        yield put(pdApiActions.setKceVersions(kceVersions));
        break;
      }
      case "driveVersions": {
        const driveVersions = _.values(pdApi.driveVersions).filter(
          driveVersion => driveVersion.id !== id
        );
        yield put(pdApiActions.setDriveVersions(driveVersions));
        break;
      }
      case "signals": {
        const signals = _.values(pdApi.signals).filter(signal => signal.id !== id);
        yield put(pdApiActions.setDriveSignals(signals));
        break;
      }
      case "aadgroups": {
        const aadgroups = _.values(pdApi.aadgroups).filter(aadgroup => aadgroup.id !== id);
        yield put(pdApiActions.setAADGroups(aadgroups));
        break;
      }
      case "driveTests": {
        const driveTest = _.values(pdApi.driveTests).filter(test => test.id !== id);
        yield put(pdApiActions.setDriveTests(driveTest));
        break;
      }
      case "signalGroups": {
        const signalGroups = _.values(pdApi.signalGroups).filter(
          signalGroup => signalGroup.id !== id
        );
        yield put(pdApiActions.setDriveSignalGroups(signalGroups));
        yield put(pdApiActions.fetchSignals());
        break;
      }
      case "escalatorRTDs": {
        const escalatorRTDs = _.values(pdApi.escalatorRTDs).filter(escalatorRTD => escalatorRTD.id !== id);
        yield put(pdApiActions.setEscalatorRTDs(escalatorRTDs));
        break;
      }
      case "escalatorRTDGroups": {
        const escalatorRTDGroups = _.values(pdApi.escalatorRTDGroups).filter(
          escalatorRTDGroup => escalatorRTDGroup.id !== id
        );
        yield put(pdApiActions.setEscalatorRTDGroups(escalatorRTDGroups));
        //yield put(pdApiActions.fetchEscalatorRTDs());//check this
        break;
      }
      case "escalatorEventLogs": {
        const escalatorEventLogs = _.values(pdApi.escalatorEventLogs).filter(escalatorEventLog => escalatorEventLog.id !== id);
        yield put(pdApiActions.setEscalatorEventLogs(escalatorEventLogs));
        break;
      }
      case "users": {
        const users = pdApi.users.filter(user => user.id !== id);
        yield put(pdApiActions.setUsers(users));
        break;
      }
      case "eventClasses": {
        const eventClasses = pdApi.eventClasses.filter(eventClass => eventClass.enumValue !== id);
        yield put(pdApiActions.setEventClasses(eventClasses));
        break;
      }
      case "eventTypes": {
        const eventTypes = pdApi.eventTypes.filter(eventType => eventType.enumValue !== id);
        yield put(pdApiActions.setEventTypes(eventTypes));
        break;
      }
      case "electrifications": {
        const electrifications = pdApi.electrifications.filter(
          electrification => electrification.enumValue !== id
        );
        yield put(pdApiActions.setElectrifications(electrifications));
        break;
      }
      case "eventPids": {
        const eventPids = pdApi.eventPids.filter(eventPid => eventPid.enumValue !== id);
        yield put(pdApiActions.setEventPids(eventPids));
        break;
      }
      case "extCommands": {
        const extCommands = pdApi.extCommands.filter(extCommand => extCommand.enumValue !== id);
        yield put(pdApiActions.setExtCommands(extCommands));
        break;
      }
      case "messageTypes": {
        const messageTypes = pdApi.messageTypes.filter(mt => mt.enumValue !== id);
        yield put(pdApiActions.setMessageTypes(messageTypes));
        break;
      }
      case "ioBoardTypes": {
        const ioBoardTypes = pdApi.ioBoardTypes.filter(
          ioBoardType => ioBoardType.boardTypeId !== id
        );
        yield put(pdApiActions.setIOBoardTypes(ioBoardTypes));
        break;
      }
      case "ioCpuEnums": {
        const ioCpuEnums = pdApi.ioCpuEnums.filter(ioCpu => ioCpu.enumValue !== id);
        yield put(pdApiActions.setIOCpus(ioCpuEnums));
        break;
      }
      case "ioNetworks": {
        const ioNetworks = pdApi.ioNetworks.filter(ioNetwork => ioNetwork.enumValue !== id);
        yield put(pdApiActions.setIONetwork(ioNetworks));
        break;
      }
      case "ioOptions": {
        const ioOptions = pdApi.ioOptions.filter(ioOption => ioOption.id !== id);
        yield put(pdApiActions.setIOOptions(ioOptions));
        break;
      }
      case "ioOptionsEnumType": {
        const ioOptionsEnumType = pdApi.ioOptionsEnumType.filter(ioOptionsEnumType => ioOptionsEnumType.enumID !== id);
        yield put(pdApiActions.setIOEnumOptionsType(ioOptionsEnumType));
        break;
      }
      case "ioDataTypes": {
        const ioDataTypes = pdApi.ioDataTypes.filter(ioDataType => ioDataType.id !== id);
        yield put(pdApiActions.setIODatatypes(ioDataTypes));
        break;
      }
      case "ioBasicDataTypes": {
        const ioBasicDataTypes = pdApi.ioBasicDataTypes.filter(
          IoBasicDataType => IoBasicDataType.id !== id
        );
        yield put(pdApiActions.setIOBasicDatatypes(ioBasicDataTypes));
        break;
      }
      case "iodataSections": {
        const iodataSections = pdApi.iodataSections.filter(
          iodataSections => iodataSections.id !== id
        );
        yield put(pdApiActions.setIODataSections(iodataSections));
        break;
      }
      case "ioConfigurations": {
        if (ioConfig.ioConfigurations) {
          const ioConfigurations = ioConfig?.ioConfigurations.filter(
            ioConfiguration => ioConfiguration.id !== id
          );
          yield put(ioConfigActions.setIoConfigurations(ioConfigurations));
          yield put(ioConfigActions.setSelectedIoConfigurationId(""));
        }
        break;
      }
      case "standardBoards": {
        const standardBoards = ioConfig.standardBoards.filter(
          standardBoard => standardBoard.id !== id
        );
        yield put(ioConfigActions.setStandardBoards(standardBoards));
        break;
      }
      case "standardPoints": {
        const standardPoints = ioConfig.standardPoints.filter(
          standardPoint => standardPoint.id !== id
        );
        yield put(ioConfigActions.setStandardPoints(standardPoints));
        break;
      }
      case "ionodePoints": {
        const ionodePoints = ioConfig.ionodePoints.filter(ionodePoint => ionodePoint.id !== id);
        yield put(ioConfigActions.setIonodePoints(ionodePoints));
        break;
      }
      case "standardActions": {
        const points: Api.StandardPoint[] = ioConfig.standardPoints.map(point => {
          const FilteredActions = point.standardActions?.filter(
            (action: any) => action.id !== id
          );
          return { ...point, standardActions: FilteredActions ?? [] };
        });

        yield put(ioConfigActions.setStandardPoints(points));
        break;
      }

      case "ionodePointActions": {
        const ioNodePoints: Api.IoNodePoint[] = ioConfig.ionodePoints.map(point => {
          const FilteredActions = point.ionodePointActions?.filter(
            (action: any) => action.id !== id
          );
          return { ...point, ionodePointActions: FilteredActions ?? [] };
        });
        yield put(ioConfigActions.setIonodePoints(ioNodePoints));
        break;
      }

      case "ioBoards": {
        yield put(ioConfigActions.setIonodePoints([]));
        break;
      }

      case "enumerations": {
        const enumerations = pdApi.enumerations.filter(enumeration => enumeration.id !== id);
        yield put(pdApiActions.setCommonEnumerations(enumerations));
        yield put(editActions.selectParent(undefined));
        break;
      }
      case "enumerationItems": {
        const enumerationItems = pdApi.enumerationItems.filter(
          enumerationItem => enumerationItem.id !== id
        );
        yield put(pdApiActions.setCommonEnumerationItems(enumerationItems));
        // we also need to fetch enumerations to update the in-memory data
        const updatedEnumerations = yield call(pdApiGet, "enumerations");
        yield put(pdApiActions.setCommonEnumerations(updatedEnumerations));
        break;
      }
      case "doorEventClasses": {
        const doorEventClasses = pdApi.doorEventClasses.filter(doorEventClass => doorEventClass.enumValue !== id);
        yield put(pdApiActions.setDoorEventClasses(doorEventClasses));
        break;
      }
      case "doorEventTypes": {
        const doorEventTypes = pdApi.doorEventTypes.filter(doorEventType => doorEventType.enumValue !== id);
        yield put(pdApiActions.setDoorEventTypes(doorEventTypes));
        break;
      }
      case "doorEventPids": {
        const doorEventPids = pdApi.doorEventPids.filter(doorEventPid => doorEventPid.enumValue !== id);
        yield put(pdApiActions.setDoorEventPids(doorEventPids));
        break;
      }
      case "doorExtCommands": {
        const doorExtCommands = pdApi.doorExtCommands.filter(doorExtCommand => doorExtCommand.enumValue !== id);
        yield put(pdApiActions.setDoorExtCommands(doorExtCommands));
        break;
      }
      case "EscalatorEventClasses": {
        const EscalatorEventClasses = (pdApi.EscalatorEventClasses ?? []).filter(EscalatorEventClasses => EscalatorEventClasses.enumValue !== id);
        yield put(pdApiActions.setEscalatorEventClasses(EscalatorEventClasses));
        break;
      }
      case "EscalatorEventTypes": {
        const EscalatorEventTypes = (pdApi.EscalatorEventTypes ?? []).filter(EscalatorEventTypes => EscalatorEventTypes.enumValue !== id);
        yield put(pdApiActions.setEscalatorEventTypes(EscalatorEventTypes));
        break;
      }
      case "EscalatorEventPids": {
        const EscalatorEventPids = (pdApi.EscalatorEventPids ?? []).filter(EscalatorEventPid => EscalatorEventPid.enumValue !== id);
        yield put(pdApiActions.setEscalatorEventPids(EscalatorEventPids));
        break;
      }
      case "EscalatorExtCommands": {
        const EscalatorExtCommands = (pdApi.EscalatorExtCommands ?? []).filter(EscalatorExtCommand => EscalatorExtCommand.enumValue !== id);
        yield put(pdApiActions.setEscalatorExtCommand(EscalatorExtCommands));
        break;
      }
      case "EscalatorRequestTypes": {
        const EscalatorRequestTypes = (pdApi.EscalatorRequestTypes ?? []).filter(EscalatorRequestType => EscalatorRequestType.enumValue !== id);
        yield put(pdApiActions.setEscalatorRequestTypes(EscalatorRequestTypes));
        break;
      }
      case "EscalatorVersions": {
        const escalatorVersions = _.values(pdApi.EscalatorVersions).filter(
          escalatorVersion => escalatorVersion.id !== id
        );
        yield put(pdApiActions.setEscalatorVersions(escalatorVersions));
        break;
      }
      case "DoorVersions": {
        const DoorVersions = (pdApi.DoorVersions ?? []).filter(DoorVersion => DoorVersion.id !== id);
        yield put(pdApiActions.setDoorVersions(DoorVersions));
        break;
      }
      case "dataActs": {
        const dataActs = (pdApi.dataActCodes ?? []).filter(dataAct => dataAct.id !== id);
        yield put(pdApiActions.setDataActCodes(dataActs));
        break;
      }
      case "escalatorDataActs": {
        const escalatorDataActs = (pdApi.escalatorDataActCodes ?? []).filter(escalatorDataAct => escalatorDataAct.id !== id);
        yield put(pdApiActions.setEscalatorDataActCodes(escalatorDataActs));
        break;
      }
      case "grandNorms": {
        const grandNorms = (pdApi.grandNorms ?? []).filter(grandNorm => grandNorm.id !== id);
        yield put(pdApiActions.setGrandNorms(grandNorms));
        break;
      }
      default: {
        console.error("Something went wrong in delete saga");
        const errorMsg = { message: "Something went wrong in delete saga" };
        throw errorMsg;
      }
    }
    yield put(showSuccess(`DELETED: ${noticationMessages[option](editedItem)}`));
  } catch (e) {
    if (!e.response?.data?.status)//system error
    {
      yield put(showError(`DELETE FAILED: ${noticationMessages[option](editedItem)}`, { autoDismiss: 5 }));
    } else if (e.response?.data?.status) {  //backend framed error
      if (e.response.data.status >= 400 && e.response.data.status < 500 && e.response.data.error?.length > 0) {
        yield put(showError(`${e.response.data.error}`, { autoDismiss: 10 }));
      } else {
        yield put(showError(`DELETE FAILED: ${noticationMessages[option](editedItem)}`, { autoDismiss: 5 }));
      }
    }
  } finally {
    yield put(editActions.setEdited(undefined));
    yield put(editActions.setStored(undefined));
    yield put(mainActions.setFetchingStatus(undefined));
  }
}

/** Start editing */
export function* startEditingSaga(
  action: ReturnType<typeof editActions.startEditing>
): Api.AnyItem {
  const { object, isNew } = action.payload;
  if (!object) {
    yield put(editActions.setEdited(undefined));
    yield put(editActions.setStored(undefined));
  } else {
    if (!isNew) {
      yield put(editActions.setStored(object));
    } else {
      // For adding a new item id-field is set by back-end
      object.id = undefined;
      yield put(editActions.setStored(undefined));
    }
    yield put(editActions.setEdited(object));
  }
}

/** Cancel editing */

export function* cancelEditingSaga(): Api.AnyItem {
  yield put(editActions.setEdited(undefined));
  yield put(editActions.setStored(undefined));
}

/* Select saga */
export function* selectSaga(action: ReturnType<typeof editActions.select>): Api.AnyItem {
  const editedObject = yield select(editViewSelectors.editedObject);
  const storedObject = yield select(editViewSelectors.storedObject);
  const object = action.payload;
  const hasEdited = !_.isEqual(editedObject, storedObject);
  const idKey = "id";
  // TODO: fix types
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (editedObject && object[idKey] === editedObject[idKey]) {
    yield put(editActions.setEdited(undefined));
    yield put(editActions.setStored(undefined));
  } else if (hasEdited) {
    yield put(showWarning("Cannot select new entry when another has been edited"));
  } else {
    yield put(editActions.startEditing(object, false));
  }
}

/* Select parent saga */
export function* selectParentSaga(action: ReturnType<typeof editActions.select>): Api.AnyItem {
  const selectedParentObject = yield select(editViewSelectors.parentObject);
  const object = action.payload;
  const idKey = "id";
  // TODO: fix types
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (!object || (selectedParentObject && object[idKey] === selectedParentObject[idKey])) {
    yield put(editActions.setParent(undefined));
  } else {
    yield put(editActions.setParent(object));
  }
}
